// License: copied from https://github.com/tinymce/tinymce-react
import { eventPropTypes } from './components/EditorPropTypes';
/**
 * Checks if the given input is a function.
 *
 * @param {unknown} x - The value to check.
 * @returns {x is Function} True if the input is a function; otherwise, false.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const isFunction = (x) => typeof x === 'function';
const isEventProp = (name) => name in eventPropTypes;
const eventAttrToEventName = (attrName) => attrName.substr(2);
/**
 * Configures event handlers by comparing previous and current props, registering new handlers and unregistering removed ones.
 *
 * @template H - The handler type.
 * @template K
 *
 * @param {PropLookup} handlerLookup - Object used to look up event handler definitions.
 * @param {(name: string, handler: H) => void} on - Function to register an event handler.
 * @param {(name: string, handler: H) => void} off - Function to unregister an event handler.
 * @param {(lookup: PropLookup, key: string) => H} adapter - Function to wrap a raw handler prop for compatibility.
 * @param {any} prevProps - The previous props containing event handlers.
 * @param {any} props - The current props containing event handlers.
 * @param {Record<string, H>} boundHandlers - A record tracking currently bound event handlers.
 *
 * @returns {void}
 */
export const configHandlers2 = (handlerLookup, on, off, adapter, 
// TODO: fix tinymce types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
prevProps, 
// TODO: fix tinymce types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
props, boundHandlers) => {
    const prevEventKeys = Object.keys(prevProps).filter(isEventProp);
    const currEventKeys = Object.keys(props).filter(isEventProp);
    const removedKeys = prevEventKeys.filter((key) => props[key] === undefined);
    const addedKeys = currEventKeys.filter((key) => prevProps[key] === undefined);
    removedKeys.forEach((key) => {
        // remove event handler
        // TODO: fix tinymce types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventName = eventAttrToEventName(key);
        const wrappedHandler = boundHandlers[eventName];
        off(eventName, wrappedHandler);
        delete boundHandlers[eventName];
    });
    addedKeys.forEach((key) => {
        const wrappedHandler = adapter(handlerLookup, key);
        // TODO: fix tinymce types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventName = eventAttrToEventName(key);
        boundHandlers[eventName] = wrappedHandler;
        on(eventName, wrappedHandler);
    });
};
/**
 * Configures event handlers for a TinyMCE editor instance by comparing previous and current props.
 *
 * @param {any} editor - The TinyMCE editor instance.
 * @param {Partial<IAllProps>} prevProps - The previously set props for the editor.
 * @param {Partial<IAllProps>} props - The current props for the editor.
 * @param {{[key:string]: (event: any) => unknown}} boundHandlers - A map of bound event handlers.
 * @param {PropLookup} lookup - A lookup object used to retrieve prop-based event handlers.
 *
 * @returns {void}
 */
export const configHandlers = (
// TODO: fix tinymce types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
editor, prevProps, props, 
// TODO: fix tinymce types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
boundHandlers, lookup) => configHandlers2(lookup, editor.on.bind(editor), editor.off.bind(editor), 
// TODO: fix tinymce types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(handlerLookup, key) => (e) => { var _a; return (_a = handlerLookup(key)) === null || _a === void 0 ? void 0 : _a(e, editor); }, prevProps, props, boundHandlers);
let unique = 0;
/**
 * Generates a unique identifier string with a given prefix.
 *
 * The ID is composed of the prefix, a random number, an incrementing counter, and a timestamp.
 * Useful for generating DOM element IDs, keys, or other temporary unique identifiers.
 *
 * @param {string} prefix - A string to prepend to the generated ID.
 * @returns {string} A unique identifier string.
 */
export const uuid = (prefix) => {
    const time = Date.now();
    const random = Math.floor(Math.random() * 1000000000);
    unique++;
    return prefix + '_' + random + unique + String(time);
};
/**
 * Type guard to determine if a given element is a `<textarea>` or `<input>`.
 *
 * Useful for narrowing down DOM elements when handling user input.
 *
 * @param {Element | null} element - The DOM element to check.
 * @returns {element is HTMLTextAreaElement | HTMLInputElement} True if the element is a textarea or input.
 */
export const isTextareaOrInput = (element) => element !== null && (element.tagName.toLowerCase() === 'textarea' || element.tagName.toLowerCase() === 'input');
const normalizePluginArray = (plugins) => {
    if (typeof plugins === 'undefined' || plugins === '') {
        return [];
    }
    return Array.isArray(plugins) ? plugins : plugins.split(' ');
};
/**
 * Merges two plugin lists into a single normalized array of plugin names.
 *
 * Both `initPlugins` and `inputPlugins` can be a string (comma-separated) or an array.
 * The function ensures both are converted into arrays and then concatenates them.
 *
 * @param {string | string[] | undefined} initPlugins - Initial list of plugins.
 * @param {string | string[] | undefined} inputPlugins - Additional list of plugins.
 * @returns {string[]} Combined and normalized list of plugins.
 */
export const mergePlugins = (initPlugins, inputPlugins) => normalizePluginArray(initPlugins).concat(normalizePluginArray(inputPlugins));
// TODO: fix tinymce types
/**
 * Checks if the `beforeinput` event is supported by verifying the existence
 * of `InputEvent.prototype.getTargetRanges`. This is part of the Input Events Level 2 spec.
 *
 * @returns {boolean} `true` if `getTargetRanges` is available on `InputEvent.prototype`, otherwise `false`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isBeforeInputEventAvailable = () => window.InputEvent && typeof InputEvent.prototype.getTargetRanges === 'function';
/**
 * Checks if a DOM node is currently connected to the document.
 *
 * Falls back to a manual parent traversal check for older browsers
 * that do not support `Node.isConnected` (e.g., Internet Explorer or Edge Legacy).
 *
 * @param {Node} elem - The DOM node to check.
 * @returns {boolean} `true` if the node is in the document, otherwise `false`.
 */
export const isInDoc = (elem) => {
    if (!('isConnected' in Node.prototype)) {
        // Fallback for IE and old Edge
        let current = elem;
        let parent = elem.parentNode;
        while (parent != null) {
            current = parent;
            parent = current.parentNode;
        }
        return current === elem.ownerDocument;
    }
    return elem.isConnected;
};
// TODO: fix tinymce types
/**
 * Sets the mode of a TinyMCE editor instance.
 *
 * Supports both TinyMCE 5+ (`editor.mode.set(mode)`) and TinyMCE 4 (`editor.setMode(mode)`).
 *
 * @param {any | undefined} editor - The TinyMCE editor instance. May be undefined.
 * @param {'readonly' | 'design'} mode - The editor mode to apply. `'readonly'` disables editing, `'design'` enables it.
 * @returns {void}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setMode = (editor, mode) => {
    if (editor !== undefined) {
        if (editor.mode != null && typeof editor.mode === 'object' && typeof editor.mode.set === 'function') {
            editor.mode.set(mode);
        }
        else {
            // support TinyMCE 4
            // TODO: fix tinymce types
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            editor.setMode(mode);
        }
    }
};
